import { useEffect, useState } from "react";
import { Card, CardContent } from "../../ui/card";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { Badge } from "../../ui/badge";
import { Skeleton } from "../../ui/skeleton";
import { Search, FileText, User, CalendarIcon, RefreshCw } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Calendar } from "../../ui/calendar";
import { format } from "date-fns";
import { cn } from "../../../utils/utils";
import TenantLists from "../TenantLists";
import { getPlatformlogs } from "./Api/platformauditlogsapi";
import { ServerAuditLog } from "./interface";
import { Monitor } from "react-feather";

export default function PlatformAuditLogsDetails() {
    const [searchTerm, setSearchTerm] = useState("");
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);
    const [selectedTenant, setSelectedTenant] = useState("");
    const [serverAuditLogs, setServerAuditLogs] = useState<ServerAuditLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 50;

    const fetchPlatformLogs = async (tenantName: string) => {
        setLoading(true);
        try {
            const response: ServerAuditLog[] = await getPlatformlogs();
            setServerAuditLogs(response || []);
        } catch (err) {
            console.error("Error fetching platform logs:", err);
            setServerAuditLogs([]);
        } finally {
            setLoading(false);
        }
    };


    const getActionKeyword = (action: string): { keyword: string; variant: "success" | "destructive" | "warning" | "info" | "default" } | null => {
        const actionLower = action.toLowerCase();
        console.log("Determining keyword for action:", action);


        if (actionLower.includes("approved") || actionLower.includes("added")) {
            return { keyword: "APPROVED", variant: "success" };
        }
        if (actionLower.includes("removed") || actionLower.includes("revoked") || actionLower.includes("decline") || actionLower.includes("remove")) {
            return { keyword: "REMOVED", variant: "destructive" };
        }
        if (actionLower.includes("updated") || actionLower.includes("changed") || actionLower.includes("modified")) {
            return { keyword: "UPDATED", variant: "warning" };
        }
        if (actionLower.includes("connected")) {
            return { keyword: "CONNECTED", variant: "info" };
        }
        if (actionLower.includes("request")) {
            return { keyword: "REQUEST", variant: "default" };
        }
        if (actionLower.includes("deleted") || actionLower.includes("delete")) {
            return { keyword: "DELETED", variant: "destructive" };
        }
        if (actionLower.includes("created") || actionLower.includes("create")) {
            return { keyword: "CREATED", variant: "success" };
        }
        return null;
    };
    const getBadgeStyles = (variant: string) => {
        switch (variant) {
            case "success":
                return "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800";
            case "destructive":
                return "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800";
            case "warning":
                return "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800";
            case "info":
                return "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800";
            default:
                return "bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-800";
        }
    };


    useEffect(() => {
        if (!selectedTenant) return;
        fetchPlatformLogs(selectedTenant);
    }, [selectedTenant]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, startDate, endDate, selectedTenant]);

    // Filter logs safely
    const filteredLogs = serverAuditLogs.filter(log => {
        const matchesSearch =
            searchTerm === "" ||
            (log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
            (log.loggedInUserEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
            (log.computerUserEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
            (log.computerName?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
            (log.resourceName?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);

        let matchesDateRange = true;
        if (startDate || endDate) {
            try {
                const logDate = log.time ? new Date(log.time) : null;
                if (logDate) {
                    logDate.setHours(0, 0, 0, 0);
                    if (startDate) {
                        const start = new Date(startDate);
                        start.setHours(0, 0, 0, 0);
                        if (logDate < start) matchesDateRange = false;
                    }
                    if (endDate && matchesDateRange) {
                        const end = new Date(endDate);
                        end.setHours(23, 59, 59, 999);
                        if (logDate > end) matchesDateRange = false;
                    }
                }
            } catch {
                matchesDateRange = false;
            }
        }

        return matchesSearch && matchesDateRange;
    });

    const formatTime = (timeString?: string) => {
        if (!timeString) return "-";
        try {
            const date = new Date(timeString);
            return date.toLocaleString();
        } catch {
            return timeString;
        }
    };

    const totalPages = Math.max(1, Math.ceil(filteredLogs.length / PAGE_SIZE));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * PAGE_SIZE;
    const paginatedLogs = filteredLogs.slice(startIndex, startIndex + PAGE_SIZE);

    const handleRefresh = async () => {
        if (!selectedTenant) return;
        setCurrentPage(1);
        setSearchTerm("");
        setStartDate(undefined);
        setEndDate(undefined);
        await fetchPlatformLogs(selectedTenant);
    };

    return (
        <div className="flex-1 space-y-6 p-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800 rounded-xl flex items-center justify-center border border-purple-200 dark:border-purple-700">
                        <FileText className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight" data-testid="text-platform-logs-title">
                            Platform Audit Logs
                        </h1>
                        <p className="text-muted-foreground">
                            View activity performed by admin's and users in whiteswan platform.
                        </p>
                    </div>
                </div>
                <Button
                    variant="outline"
                    onClick={handleRefresh}
                    disabled={loading || !selectedTenant}
                    data-testid="button-refresh-logs"
                >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            {/* Filters */}
            <Card className="border-border/40">

                <CardContent className="space-y-4 p-2">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="text-sm font-medium mb-2 block">Tenant List</label>

                            <TenantLists onSelectorChange={(tenant: string) => setSelectedTenant(tenant)} isLabelNeeded={false} />
                        </div>

                        <div className="relative">
                            <label className="text-sm font-medium mb-2 block">Search</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                <Input
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10"
                                    data-testid="input-search-logs"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-2 block">Start Date</label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className={cn(
                                            "w-full justify-start text-left font-normal",
                                            !startDate && "text-muted-foreground"
                                        )}
                                        data-testid="button-start-date"
                                    >
                                        <CalendarIcon className="w-4 h-4 mr-2" />
                                        {startDate ? format(startDate, "MMM dd") : "Start date"}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0">
                                    <Calendar
                                        mode="single"
                                        selected={startDate}
                                        onSelect={(date) => {
                                            setStartDate(date);
                                            setEndDate(undefined);
                                        }}
                                        initialFocus
                                        disabled={(date) => {
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            return date < today;
                                        }}
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-2 block">End Date</label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className={cn(
                                            "w-full justify-start text-left font-normal",
                                            !endDate && "text-muted-foreground"
                                        )}
                                        data-testid="button-end-date"
                                    >
                                        <CalendarIcon className="w-4 h-4 mr-2" />
                                        {endDate ? format(endDate, "MMM dd") : "End date"}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0">
                                    <Calendar
                                        mode="single"
                                        selected={endDate}
                                        onSelect={setEndDate}
                                        initialFocus
                                        disabled={(date) => {
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            if (date < today) return true;
                                            if (!startDate) return false;
                                            return date <= startDate;
                                        }}
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>


                    </div>
                </CardContent>
            </Card>

            {/* Logs Table */}
            <Card className="border-border/40">
                <CardContent className="p-0">
                    <div className="w-full">
                        <Table className="w-full table-fixed">
                            <TableHeader>
                                <TableRow className="bg-muted/30 hover:bg-muted/30">
                                    <TableHead className="font-semibold text-foreground w-[15%]">Time</TableHead>
                                    <TableHead className="font-semibold text-foreground w-[25%]">User / Computer User</TableHead>
                                    <TableHead className="font-semibold text-foreground w-[15%]">Computer Name</TableHead>
                                    <TableHead className="font-semibold text-foreground w-[20%]">Resource</TableHead>
                                    <TableHead className="font-semibold text-foreground w-[25%]">Action</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    Array.from({ length: PAGE_SIZE }).map((_, idx) => (
                                        <TableRow key={`loading-${idx}`}>
                                            <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-36" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                                        </TableRow>
                                    ))
                                ) : paginatedLogs.map((log) => {
                                    const actionKeyword = getActionKeyword(log.action || "");

                                    return (
                                        <TableRow key={log.id} className="hover-elevate cursor-pointer" data-testid={`row-log-${log.id}`}>
                                            <TableCell className="text-xs whitespace-nowrap text-muted-foreground align-top">
                                                {formatTime(log.time)}
                                            </TableCell>
                                            <TableCell className="text-sm align-top break-all">
                                                <div className="space-y-1">
                                                    {log.loggedInUserEmail && (
                                                        <div className="flex items-center gap-1.5">
                                                            <User className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                                            <span className="font-medium break-all">{log.loggedInUserEmail}</span>
                                                        </div>
                                                    )}
                                                    {log.computerUserEmail && (
                                                        <div className={`flex items-center gap-1.5 ${log.computerUserEmail ? "text-xs text-muted-foreground" : ""}`}>
                                                            <Monitor className="w-3.5 h-3.5 flex-shrink-0" />
                                                            <span className={log.computerUserEmail ? "font-medium break-all" : ""}>{log.computerUserEmail}</span>
                                                        </div>
                                                    )}
                                                    {!log.loggedInUserEmail && !log.computerUserEmail && (
                                                        <span className="text-muted-foreground">-</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm align-top break-all">
                                                {log.computerName ? (
                                                    <div className="flex items-center gap-1.5">
                                                        <Monitor className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                                        <span className="font-medium break-all">{log.computerName}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted-foreground">-</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="align-top break-all">
                                                <div className="space-y-1">
                                                    {log.resourceType && (
                                                        <Badge variant="secondary" className="text-xs">
                                                            {log.resourceType}
                                                        </Badge>
                                                    )}
                                                    {log.resourceName && (
                                                        <div className="font-mono text-xs text-muted-foreground break-all" title={log.resourceName || ""}>
                                                            {log.resourceName}
                                                        </div>
                                                    )}
                                                    {!log.resourceType && !log.resourceName && (
                                                        <span className="text-muted-foreground">-</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="align-top break-all">
                                                <div className="flex items-start gap-2">
                                                    {log.action && actionKeyword && (
                                                        <Badge
                                                            className={`text-xs font-semibold flex-shrink-0 ${getBadgeStyles(actionKeyword.variant)}`}
                                                        >
                                                            {actionKeyword.keyword}
                                                        </Badge>
                                                    )}
                                                    <span className="text-sm flex-1 break-all">
                                                        {log.action} {actionKeyword?.keyword ? ` ${actionKeyword.keyword}` : ""}
                                                    </span>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>

                    {!loading && filteredLogs.length === 0 && (
                        <div className="text-center py-12 text-muted-foreground">
                            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <div className="text-lg font-medium">No logs found</div>
                            <div className="text-sm">Try adjusting your search criteria</div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Results Summary */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-sm text-muted-foreground">
                <div>
                    Showing {filteredLogs.length === 0 ? 0 : startIndex + 1}-{Math.min(startIndex + PAGE_SIZE, filteredLogs.length)} of {filteredLogs.length} filtered entries ({serverAuditLogs.length} total)
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={safeCurrentPage <= 1 || loading}
                        data-testid="button-prev-page"
                    >
                        Previous
                    </Button>
                    <span className="whitespace-nowrap">Page {safeCurrentPage} of {totalPages}</span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={safeCurrentPage >= totalPages || loading}
                        data-testid="button-next-page"
                    >
                        Next
                    </Button>
                </div>
            </div>
        </div>
    );
}
